/**
 * Wizard State Manager
 * Handles saving, loading, and clearing wizard progress in localStorage
 */

const WIZARD_STORAGE_KEY = 'test-profile-wizard-state';

export const wizardStateManager = {
  /**
   * Save wizard progress to localStorage
   * @param {number} currentStep - Current step number (1-7)
   * @param {object} data - Wizard data object containing all form data
   */
  saveProgress(currentStep, data) {
    try {
      const state = {
        currentStep,
        data,
        lastSaved: new Date().toISOString()
      };
      localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(state));
      console.log('✅ Wizard progress saved:', { currentStep, dataKeys: Object.keys(data) });
      return true;
    } catch (error) {
      console.error('Error saving wizard progress:', error);
      return false;
    }
  },

  /**
   * Load wizard progress from localStorage
   * @returns {object|null} - Saved wizard state or null if none exists
   */
  loadProgress() {
    try {
      const saved = localStorage.getItem(WIZARD_STORAGE_KEY);
      if (!saved) {
        return null;
      }

      const state = JSON.parse(saved);
      console.log('✅ Wizard progress loaded:', {
        currentStep: state.currentStep,
        lastSaved: state.lastSaved
      });
      return state;
    } catch (error) {
      console.error('Error loading wizard progress:', error);
      return null;
    }
  },

  /**
   * Clear wizard progress from localStorage
   */
  clearProgress() {
    try {
      localStorage.removeItem(WIZARD_STORAGE_KEY);
      console.log('✅ Wizard progress cleared');
      return true;
    } catch (error) {
      console.error('Error clearing wizard progress:', error);
      return false;
    }
  },

  /**
   * Check if wizard progress exists
   * @returns {boolean}
   */
  hasProgress() {
    return localStorage.getItem(WIZARD_STORAGE_KEY) !== null;
  },

  /**
   * Validate step data before allowing navigation
   * @param {number} step - Step number to validate
   * @param {object} data - Wizard data object
   * @returns {object} - { valid: boolean, errors: string[] }
   */
  validateStepData(step, data) {
    const errors = [];

    switch (step) {
      case 0: // Account Selection
        if (!data.selectedAccount) {
          errors.push('Please select a test account before proceeding');
        }
        break;

      case 1: // Profile Input
        if (!data.profile?.name?.trim()) {
          errors.push('Student name is required');
        }
        if (!data.profile?.graduation_year) {
          errors.push('Graduation year is required');
        }
        if (!data.profile?.state_of_residency) {
          errors.push('State of residency is required');
        }
        if (!data.profile?.intended_major?.trim()) {
          errors.push('Intended major is required');
        }
        if (!data.profile?.gpa_weighted && !data.profile?.gpa_unweighted) {
          errors.push('At least one GPA value is required');
        }
        break;

      case 2: // Parent Insights
        // At least one parent observation should be filled
        const hasParentAData = data.parentInsights?.parentA &&
          Object.values(data.parentInsights.parentA).some(val => val?.trim());
        const hasParentBData = data.parentInsights?.parentB &&
          Object.values(data.parentInsights.parentB).some(val => val?.trim());

        if (!hasParentAData && !hasParentBData) {
          errors.push('At least one parent observation is required');
        }
        break;

      case 3: // Strategic Context
        if (!data.strategicContext?.summary?.trim()) {
          errors.push('Strategic context summary is required');
        }
        // Note: personalityProfile and academicFocus are optional metadata
        // Only the summary is required
        break;

      case 4: // Question Set
        if (!data.questionSet || data.questionSet.length < 8) {
          console.log('❌ Validation failed for Step 4:', {
            questionSet: data.questionSet,
            length: data.questionSet?.length || 0
          });
          errors.push('At least 8 questions are required');
        } else {
          console.log('✅ Validation passed for Step 4:', {
            length: data.questionSet.length
          });
        }
        break;

      case 5: // Initial Answers
        if (!data.initialAnswers || Object.keys(data.initialAnswers).length < 8) {
          errors.push('All 8 initial questions must be answered');
        }
        break;

      case 6: // Follow-up Questions
        if (!data.followupQuestions || data.followupQuestions.length === 0) {
          errors.push('Follow-up questions must be generated');
        }
        if (!data.followupAnswers || Object.keys(data.followupAnswers).length === 0) {
          errors.push('Follow-up questions must be answered');
        }
        break;

      case 7: // Final Assessment
        // No validation needed for preview step
        break;

      default:
        errors.push('Invalid step number');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  },

  /**
   * Get a template for a test profile (user's "younger self")
   * @returns {object} - Template profile data
   */
  getTemplateProfile() {
    return {
      // Basic Info
      name: '',
      graduation_year: new Date().getFullYear() + 1,
      grade_level: '12th',
      state_of_residency: '',

      // Academic Stats
      gpa_weighted: null,
      gpa_unweighted: null,
      gpa_scale: '4.0',
      class_rank: null,
      class_size: null,

      // Test Scores
      sat_total: null,
      sat_math: null,
      sat_reading_writing: null,
      act_composite: null,
      act_math: null,
      act_english: null,
      act_reading: null,
      act_science: null,
      psat_score: null,
      national_merit_status: '',

      // Academic Rigor
      curriculum_rigor: '',
      ap_courses_taken: null,
      honors_courses: null,
      dual_enrollment_credits: null,
      ib_diploma_candidate: false,
      ib_predicted_score: null,

      // Interests
      intended_major: '',
      intended_minor: '',
      intended_career: '',
      academic_interests: [],
      academic_strengths: [],
      career_goals: '',
      learning_style: '',
      unique_qualities: '',

      // Activities
      activities: [],

      // Honors
      honors: [],

      // High School Context
      high_school_name: '',
      high_school_city: '',
      high_school_state: '',
      hs_competitiveness: '',

      // Additional
      special_talents: '',
      languages: '',
      special_circumstances: '',
      first_generation_college: false,
      underrepresented_background: false,
      legacy_connections: ''
    };
  }
};
